import { archiveNotificationsBulkActionType, markNotificationsAsReadBulkActionType } from '../../../../../backend/features/inbox';
import { showAlertNotificationRowDialog } from '../../../../../ui/components/AlertNotificationRowDialog';
import { useAlertUiTranslation } from '../../../../../ui/hooks';
import { getNotificationBulkActionParams } from '../../../../../ui/utils';
import { useDataTableUsingUrl } from '@juvare/common-data-table-ui/DataTable';
import { useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { requireAnyOrgPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { launchBulkAction } from '@juvare/platform-sdk-react/ui/features/bulk-actions';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import { Box, Typography } from '@mui/material';
import React from 'react';
import alertImage from '../../../../assets/alertImage.svg';
import { useAlertCreateButton, useAlertRefreshButton } from '../../../alerts';
import { useInboxTable } from '../../hooks';
import { InboxNotificationView } from '../InboxNotificationView/InboxNotificationView';
import { NotificationsList } from '../NotificationsList';
export const NotificationsListInbox = (props) => {
    var _a;
    const { t } = useAlertUiTranslation();
    const userState = useUserState();
    const filterConfig = useFilterConfig((builder) => {
        const search = builder.quickSearchFilter({ dataPath: 'searchText', label: t('common.search') });
        const notificationType = builder.quickFilterMultiSelect({
            dataPath: 'notificationType',
            label: t('pages.tables.filters.notificationType'),
            valueProviderType: 'classifier.messageType',
            widget: 'form.checkboxList',
        });
        const status = builder.quickFilterMultiSelect({
            dataPath: 'status',
            label: 'Status',
            values: [
                { label: 'Read', value: 'read' },
                { label: 'Unread', value: 'unread' },
                { label: 'Waiting For Response', value: 'waiting' },
            ],
            widget: 'form.checkboxList',
        });
        builder.allFiltersPanel({
            children: [search, notificationType, status],
        });
    }, [t]);
    const handleRowClickDialog = (alertRowId) => {
        showAlertNotificationRowDialog({
            type: 'notification',
            onClearActiveRow: clearActiveRow,
            onReload: reload,
            label: t('common.backToNotifications'),
            rowId: alertRowId,
        });
    };
    const staticFilterData = { archived: false };
    const { dataTableProps, appliedFilterData, clearActiveRow, reload } = useInboxTable({
        useDataTableFunction: useDataTableUsingUrl,
        staticFilterData: staticFilterData,
        filterConfig,
        handleRowClickDialog,
    });
    const selectedNotificationIds = (_a = dataTableProps.selectedRowIds) !== null && _a !== void 0 ? _a : [];
    const handleArchive = () => {
        const count = selectedNotificationIds.length > 0 ? selectedNotificationIds.length : dataTableProps.filteredRowCount;
        showConfirmDialog({
            title: t('archiveAlertsDialog.title', { count }),
            content: t('archiveAlertsDialog.message', { count, page: 'Inbox Archived' }),
            confirmLabel: t('common.archive'),
            onConfirm: () => launchBulkAction({
                type: archiveNotificationsBulkActionType,
                title: t('archiveAlertsDialog.progress', { count }),
                params: getNotificationBulkActionParams(dataTableProps, appliedFilterData, staticFilterData),
                onClose: () => {
                    var _a;
                    clearActiveRow();
                    (_a = dataTableProps.onDeselectAllRows) === null || _a === void 0 ? void 0 : _a.call(dataTableProps);
                    reload();
                },
            }),
        });
    };
    const handleMarkAsRead = () => {
        const count = selectedNotificationIds.length > 0 ? selectedNotificationIds.length : dataTableProps.filteredRowCount;
        showConfirmDialog({
            title: t('markAsReadNotificationsDialog.title', { count }),
            confirmLabel: t('common.markAsRead'),
            onConfirm: () => launchBulkAction({
                type: markNotificationsAsReadBulkActionType,
                title: t('markAsReadNotificationsDialog.progress', { count }),
                params: getNotificationBulkActionParams(dataTableProps, appliedFilterData, staticFilterData),
                onClose: () => {
                    var _a;
                    clearActiveRow();
                    (_a = dataTableProps.onDeselectAllRows) === null || _a === void 0 ? void 0 : _a.call(dataTableProps);
                    reload();
                },
            }),
        });
    };
    const actions = [
        {
            icon: React.createElement(ArchiveOutlinedIcon, null),
            label: t('common.archive'),
            onClick: handleArchive,
            'data-test': 'archive',
        },
        {
            icon: React.createElement(DraftsOutlinedIcon, null),
            label: t('inboxTable.markAsRead'),
            onClick: handleMarkAsRead,
            'data-test': 'mark-as-read',
        },
    ];
    const mainAction = useAlertCreateButton();
    const secondaryAction = useAlertRefreshButton(() => reload());
    return (React.createElement(NotificationsList, Object.assign({}, dataTableProps, { title: props.archived ? t('pages.tables.tabs.archived') : t('common.inbox'), noNotificationsLabel: props.archived ? t('common.thereAreNoArchivedAlerts') : t('common.thereAreNoReceivedNotifications'), localActions: actions, mainAction: requireAnyOrgPermission(userState, 'view-and-message-members', 'create-notifications') ? mainAction : undefined, secondaryAction: secondaryAction }), dataTableProps.activeRowId ? (React.createElement(InboxNotificationView, { id: dataTableProps.activeRowId, onReload: (clearActiveAlert) => {
            if (clearActiveAlert) {
                clearActiveRow();
            }
            reload();
        } })) : (React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, px: 2, py: '180px', alignItems: 'center' } },
        React.createElement("img", { src: alertImage, alt: '' }),
        React.createElement(Typography, { variant: 'subtitle1' }, props.archived
            ? t('common.countArchivedAlertsSelected', { count: selectedNotificationIds.length })
            : t('common.countReceivedNotificationsSelected', { count: selectedNotificationIds.length }))))));
};
